import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Building2, MonitorSmartphone, PartyPopper, Handshake, UserRound, Wrench, Megaphone, KeyRound, UserPlus, Radar, ShieldCheck, Star } from "lucide-react";
import FeedbackSection from "@/components/FeedbackSection";
import AuthCard from "@/components/AuthCard";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { icon: Building2, name: "Asset", key: "pillar_asset_d" },
  { icon: MonitorSmartphone, name: "Digital", key: "pillar_digital_d" },
  { icon: PartyPopper, name: "Experience", key: "pillar_experience_d" },
  { icon: Handshake, name: "Professions", key: "pillar_professions_d" },
];
const HERO_BULLETS = [
  { icon: UserRound, key: "hero_b_client" }, { icon: Wrench, key: "hero_b_specialist" },
  { icon: Megaphone, key: "hero_b_promoter" }, { icon: KeyRound, key: "hero_b_owner" },
];
const STEPS = [{ icon: UserPlus, n: 1 }, { icon: Radar, n: 2 }, { icon: ShieldCheck, n: 3 }];
const PROOF_MIN = { users: 10, orders: 5 };

function SocialProof({ t }) {
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/feedback/public").then((r) => setReviews(r.data)).catch(() => {});
  }, []);
  const showNumbers = stats && (stats.users >= PROOF_MIN.users || stats.orders >= PROOF_MIN.orders);
  if (!showNumbers && reviews.length === 0) return null;
  const items = [["users", stats?.users], ["specialists", stats?.specialists], ["offers", stats?.offers], ["orders", stats?.orders]];
  return (
    <section data-testid="social-proof" className="bg-[#fafafa] border-y border-neutral-200">
      <div className="mx-auto max-w-6xl px-6 py-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-8">{t("proof_title")}</h2>
        {showNumbers && (
          <div data-testid="proof-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {items.map(([k, v]) => (
              <div key={k} className="rounded-2xl bg-white border border-neutral-200 p-5">
                <p data-testid={`proof-${k}`} className="font-display text-3xl font-extrabold">+{v ?? 0}</p>
                <p className="text-sm text-neutral-500">{t(`proof_${k}`)}</p>
              </div>
            ))}
          </div>
        )}
        {reviews.length > 0 && (
          <div data-testid="proof-reviews" className="grid md:grid-cols-3 gap-4">
            {reviews.slice(0, 3).map((r) => (
              <figure key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl bg-white border border-neutral-200 p-5">
                <div className="flex gap-0.5 mb-2">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} className={n <= r.rating ? "fill-[#E0B94B] text-[#E0B94B]" : "text-neutral-300"} />)}</div>
                <blockquote className="text-sm text-neutral-700 leading-relaxed">“{r.message}”</blockquote>
                <figcaption className="mt-3 text-xs font-semibold">{r.name}</figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Landing() {
  const { t, lang, setLang } = useI18n();
  const refParam = new URLSearchParams(window.location.search).get("ref") || "";
  const [mode, setMode] = useState(() => (window.location.search.includes("register=1") || refParam ? "register" : "login"));
  const goRegister = () => { setMode("register"); document.getElementById("accedi")?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0a0a0a] text-white">
        <div className="absolute inset-0 enb-grain opacity-40" />
        <div className="relative mx-auto max-w-6xl px-6 pt-8 pb-16 md:pt-12 md:pb-24">
          <div className="flex items-center justify-between">
            <Logo size={26} dark />
            <div className="flex gap-1">
              {["it", "ro", "en"].map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
                  className={`h-8 px-2.5 rounded-full text-xs font-semibold uppercase transition-colors ${lang === l ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="mt-12 md:mt-16 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end">
            <div>
              <motion.p data-testid="hero-subtitle" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="text-sm sm:text-base text-[#d4af37] font-semibold mb-4">{t("nos_badge")}</motion.p>
              <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                {t("slogan_1")}<br />{t("slogan_2")} <span className="text-[#d4af37]">{t("slogan_3")}</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
                className="mt-5 text-base sm:text-lg text-neutral-300 max-w-xl leading-relaxed">{t("hero_desc")}</motion.p>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="mt-8 flex flex-wrap gap-3 items-center">
                <Button data-testid="hero-cta-btn" onClick={goRegister} className="h-12 rounded-full bg-white text-black hover:bg-neutral-200 px-7 text-base font-semibold">
                  {t("hero_cta")} <ArrowRight size={18} className="ml-1" />
                </Button>
                <a href="#come-funziona" data-testid="hero-cta-how" className="h-12 inline-flex items-center px-5 rounded-full border border-white/25 text-sm font-medium hover:border-white transition-colors">{t("hero_cta_2")}</a>
              </motion.div>
              <p data-testid="hero-note" className="mt-4 text-xs text-neutral-500">{t("hero_note")}</p>
            </div>
            <motion.ul initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} data-testid="hero-bullets" className="space-y-2">
              {HERO_BULLETS.map(({ icon: Icon, key }) => (
                <li key={key} className="flex items-start gap-3 rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3 backdrop-blur-sm">
                  <span className="mt-0.5 shrink-0 h-7 w-7 rounded-full bg-[#d4af37]/15 text-[#d4af37] flex items-center justify-center"><Icon size={15} /></span>
                  <span className="text-sm text-neutral-200 leading-snug">{t(key)}</span>
                </li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" data-testid="how-section" className="mx-auto max-w-6xl px-6 py-14 md:py-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("how_title")}</h2>
        <p className="text-neutral-500 mt-1 mb-8">{t("how_sub")}</p>
        <ol className="grid md:grid-cols-3 gap-4">
          {STEPS.map(({ icon: Icon, n }, i) => (
            <motion.li key={n} data-testid={`how-step-${n}`} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="relative rounded-2xl border border-neutral-200 p-6 hover:border-black transition-colors">
              <span className="absolute top-5 right-5 font-mono text-xs text-neutral-400">0{n}</span>
              <span className="h-11 w-11 rounded-full bg-black text-white flex items-center justify-center mb-4"><Icon size={20} /></span>
              <h3 className="font-display text-lg font-semibold mb-1">{t(`how_${n}_t`)}</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">{t(`how_${n}_d`)}</p>
            </motion.li>
          ))}
        </ol>
      </section>

      {/* PILLARS */}
      <section data-testid="pillars-section" className="mx-auto max-w-6xl px-6 pb-14 md:pb-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("pillars_title")}</h2>
        <p className="text-neutral-500 mt-1 mb-8">{t("pillars_sub")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.name} data-testid={`pillar-${p.name.toLowerCase()}`} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-neutral-200 p-6 hover:border-black transition-colors flex flex-col gap-3">
                <span className="h-11 w-11 rounded-2xl bg-[#fafafa] border border-neutral-200 flex items-center justify-center"><Icon size={22} strokeWidth={1.7} /></span>
                <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed"><span className="font-semibold text-neutral-800">{t("pillar_examples")}:</span> {t(p.key)} <span className="text-neutral-400">{t("pillar_more")}</span></p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <SocialProof t={t} />

      {/* AUTH */}
      <section id="accedi" className="mx-auto max-w-md px-6 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-center">{t("auth_title")}</h2>
        <p className="text-neutral-500 text-center mt-1 mb-6">{t("auth_sub")}</p>
        <AuthCard mode={mode} setMode={setMode} refParam={refParam} />
      </section>

      <FeedbackSection />
      <footer className="text-center text-xs text-neutral-400 pb-8 px-6 space-y-2">
        <p data-testid="footer-langs">{t("footer_langs")}</p>
        <p><a data-testid="referral-rules-link" href="/regolamento-affiliazione" className="underline underline-offset-2">Regolamento Programma Affiliazione</a> · <a data-testid="cashback-rules-link" href="/come-funziona-cashback" className="underline underline-offset-2">Come funziona il Cashback</a></p>
      </footer>
    </div>
  );
}
